<template>
  <div class="ProfileNavLinks leading-normal">
    <!-- Manage Content -->
    <div v-if="hasTeams" :class="isDropdownContent ? 'pb-gr-5' : 'py-gr-5 border-t border-gray-200 dark:border-gray-800'
      ">
      <div class="mb-gr-4 dropdown-heading">Manage Content</div>

      <jet-responsive-nav-link v-if="$page.props.isImpersonating" as="button" @click="stopImpersonating">
        <i class="fak fa-fw fa-nova mr-gr-4 text-center"></i>Stop Impersonating
      </jet-responsive-nav-link>

      <jet-responsive-nav-link v-else-if="$page.props.canAccessNova" root target="_blank"
        href="https://store.oneaudiobooks.com/nova">
        <i class="fak fa-fw fa-nova mr-gr-4 text-center"></i>Nova Admin
      </jet-responsive-nav-link>

      <jet-responsive-nav-link root :href="route('dashboard')" :active="route().current('dashboard')">
        <i class="fa fa-fw fa-th mr-gr-4 text-center"></i>My Dashboard
      </jet-responsive-nav-link>

      <jet-responsive-nav-link root :href="route('admin.books')" :active="route().current('admin.books')">
        <i class="fa fa-fw fa-books mr-gr-4 text-center"></i>Books
      </jet-responsive-nav-link>

      <jet-responsive-nav-link root :href="route('admin.promos')"
        :active="route().current('admin.promos') && !route().params?.b">
        <i class="fa fa-fw fa-headphones mr-gr-4 text-center"></i>Promos
      </jet-responsive-nav-link>

      <jet-responsive-nav-link v-if="$page.props.team?.can_import" root :href="route('admin.imports')"
        :active="route().current('admin.imports')">
        <i class="fa fa-fw fa-file-spreadsheet mr-gr-4 text-center"></i>Imports
      </jet-responsive-nav-link>
    </div>

    <!-- User Management -->
    <div :class="isDropdownContent && !hasTeams
        ? 'pb-gr-5'
        : 'py-gr-5 border-t border-gray-200 dark:border-gray-800'
      ">
      <div class="dropdown-heading">My Account</div>
      <div class="mb-gr-5 leading-[1] px-gr-5 text-xs text-gray-500">
        {{ $page.props.user.email }}
      </div>

      <!-- <div class="flex items-center px-4">
      <div v-if="$page.props.jetstream.managesProfilePhotos" class="shrink-0 mr-3">
        <img
          class="h-10 w-10 rounded-full object-cover"
          :src="$page.props.user.avatar"
          :alt="$page.props.user.name"
        />
      </div>

      <div>
        <div class="font-medium text-base text-gray-800 dark:text-gray-200">
          {{ $page.props.user.name }}
        </div>
        <div class="font-medium text-sm text-gray-500">
          {{ $page.props.user.email }}
        </div>
      </div>
    </div> -->

      <jet-responsive-nav-link v-if="hasTeams" root :href="route('profile.show')"
        :active="route().current('profile.show')">
        <i class="fa fa-fw fa-user-circle mr-gr-4"></i>My Profile
      </jet-responsive-nav-link>

      <jet-responsive-nav-link v-else root :href="route('account.index')" :active="route().current('account.index')"
        class="relative">
        <i class="fa fa-fw fa-user-circle mr-gr-4"></i>My Account
        <template v-if="showMenuDot">
          <div class="absolute top-gr-3 left-gr-4 w-gr-4 h-gr-4 rounded-full bg-red-500"></div>
          <div class="animate-ping absolute top-gr-3 left-gr-4 w-gr-4 h-gr-4 rounded-full bg-red-500"></div>
        </template>
      </jet-responsive-nav-link>

      <jet-responsive-nav-link v-if="hasPromos" root :href="route('library.view')"
        :active="route().current('library.view')">
        <i class="fa fa-fw fa-bookmark mr-gr-4"></i>My Library
      </jet-responsive-nav-link>

      <jet-responsive-nav-link :href="route('account.personalize')">
        <i class="fa fa-fw fa-user-circle mr-gr-4"></i>Personalize
      </jet-responsive-nav-link>

      <jet-responsive-nav-link root :href="route('api-tokens.index')" :active="route().current('api-tokens.index')"
        v-if="$page.props.jetstream.hasApiFeatures">
        API Tokens
      </jet-responsive-nav-link>

      <jet-responsive-nav-link as="button" @click="toggleTheme" class="rounded-none">
        <span :class="{ hidden: isDark }" class="flex justify-between items-center w-full">
          <span><i class="fa fa-fw fa-adjust mr-gr-4"></i>Dark Mode</span>
          <i class="fas fa-fw fa-sm fa-moon mr-gr-4"></i>
        </span>
        <span :class="{ hidden: !isDark }" class="flex justify-between items-center w-full">
          <span><i class="fas fa-fw fa-adjust mr-gr-4"></i>Light Mode</span>
          <i class="text-secondary-300 fa fa-fw fa-sun mr-gr-4"></i>
        </span>
      </jet-responsive-nav-link>

      <form method="POST" @submit.prevent="logout">
        <jet-responsive-nav-link as="button">
          <i class="far fa-fw fa-sign-out mr-gr-4"></i>Logout
        </jet-responsive-nav-link>
      </form>
    </div>

    <!-- Team Management -->
    <div v-if="hasTeams && $page.props.jetstream.hasTeamFeatures"
      class="py-gr-5 border-t border-gray-200 dark:border-gray-800">
      <div class="mb-gr-4 dropdown-heading">Manage Team</div>

      <jet-responsive-nav-link v-if="$page.props.user.current_team" root
        :href="route('teams.show', $page.props.user.current_team)" :active="route().current('teams.show')">
        <i class="fa fa-fw fa-users-cog mr-gr-4"></i>Team Settings
      </jet-responsive-nav-link>

      <jet-responsive-nav-link root :href="route('teams.create')" :active="route().current('teams.create')">
        <i class="fa fa-fw fa-users-medical mr-gr-4"></i>Create New Team
      </jet-responsive-nav-link>
    </div>

    <!-- Team Switcher -->
    <div v-if="hasTeams" class="py-gr-5 border-t border-gray-200 dark:border-gray-800">
      <div class="mb-gr-4 dropdown-heading">Switch Teams</div>

      <template v-for="team in $page.props.user.all_teams" :key="team.id">
        <form @submit.prevent="switchToTeam(team)">
          <jet-responsive-nav-link as="button">
            <div class="flex items-center">
              <svg v-if="team.id == $page.props.user.current_team_id" class="mr-2 h-5 w-5 text-tertiary-400" fill="none"
                stroke-linecap="round" stroke-linejoin="round" stroke-width="2" stroke="currentColor"
                viewBox="0 0 24 24">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>{{ team.name }}</div>
            </div>
          </jet-responsive-nav-link>
        </form>
      </template>
    </div>

    <!-- Customer Support -->
    <div class="border-t border-gray-200 dark:border-gray-800"
      :class="isDropdownContent ? 'pt-gr-5 pb-gr-3' : 'py-gr-5'">
      <div class="mb-gr-4 dropdown-heading">Customer Support</div>

      <div>
        <a href="mailto:support@oneaudiobooks.com" class="responsive-nav-link" target="_blank" rel="noopener">
          <i class="fa fa-fw fa-paper-plane mr-gr-4"></i>Email Us
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import JetResponsiveNavLink from '@/Jetstream/ResponsiveNavLink';
import { getTheme, toggleTheme } from '@/Utils/darkmode';

export default {
  name: 'ProfileNavLinks',

  components: {
    JetResponsiveNavLink,
  },

  props: {
    isDropdownContent: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isDark: getTheme() === 'dark',
    };
  },

  computed: {
    hasPromos() {
      return this.$page.props?.hasPromos;
    },

    hasTeams() {
      return (
        this.$page.props?.user?.teams?.length ||
        Object.keys(this.$page.props?.user?.all_teams || {}).length
      );
    },

    showMenuDot() {
      return this.$page.props.canSetPassword && !this.hasTeams;
    },
  },

  methods: {
    logout() {
      this.$inertia.post(route('logout'));
    },

    toggleTheme() {
      let theme = toggleTheme();
      this.isDark = theme === 'dark';
    },

    switchToTeam(team) {
      let redirect = route(route()?.current(), { ...route()?.params });
      this.$inertia.put(
        route('current-team.update'),
        {
          team_id: team.id,
          redirect,
        },
        {
          preserveState: false,
        },
      );
    },

    stopImpersonating() {
      this.$inertia.delete(route('impersonate'));
    },
  },
};
</script>

<!-- <style lang="scss" src="./ProfileNavLinks.scss" scoped></style> -->
